import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import EditIcon from '@mui/icons-material/Edit';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SearchIcon from '@mui/icons-material/Search';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogContent,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import useSextforceCaptions, { SextforceCaptionCategoryRow, SextforceCaptionRow } from '../../../hooks/useSextforceCaptions';
import useSubscribers from '../../../hooks/useSubscribers';
import { stripHtml } from '../../../utils/common';
import DialogAdaptive from '../../common/DialogAdaptive';
import OnlyFansMessageInputDialog from '../../common/OnlyFansMessageInputDialog';
import StyledCardTitleBar from '../../common/StyledCardTitleBar';
import StyledDialogActions from '../../common/StyledDialogActions';
import MessageUsersFormEditiorPreview from '../messageUsers/sendMessage/MessageUsersFormEditiorPreview';
import SextforceCaptionRowLocalMenu from './SextforceCaptionRowLocalMenu';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 250];
const CAPTIONS_CHATGPT_TIP_DISMISSED_KEY = 'sextforce.captions.chatgptTip.dismissed';

type Props = {
    inDialog?: boolean;
    onLoadingChange?: (isLoading: boolean) => void;
};

export default function SextforceCaptionsManager(props: Props) {
    const { inDialog, onLoadingChange } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const params = useParams();
    const currentUserId = params.userId as string | undefined;
    const { data: subscribersData, isLoading: isSubscribersLoading, refetch: refetchSubscribers } = useSubscribers();
    const subscribersList = Array.isArray(subscribersData) ? subscribersData : (subscribersData as any)?.rows ?? [];
    const otherAccounts = currentUserId ? subscribersList.filter((s: any) => s._id !== currentUserId) : subscribersList;
    const {
        data: captions = [],
        isLoading,
        createCaption,
        updateCaption,
        deleteCaption,
        bulkDeleteCaptions,
        bulkDeleteLoading,
        importFromCsvText,
        importLoading,
        copyCaptionsToOtherAccounts,
        copyLoading,
        categories = [],
        createCategory,
        updateCategory,
        deleteCategory,
        assignCategory,
        categoryMutationLoading,
    } = useSextforceCaptions(true);
    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [filterText, setFilterText] = useState('');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [captionDialogOpen, setCaptionDialogOpen] = useState(false);
    const [captionDialogMode, setCaptionDialogMode] = useState<'add' | 'edit'>('add');
    const [captionEditRow, setCaptionEditRow] = useState<SextforceCaptionRow | null>(null);
    const [copyToAccountsOpen, setCopyToAccountsOpen] = useState(false);
    const [copyTargetUserId, setCopyTargetUserId] = useState('');
    const [copySourceIds, setCopySourceIds] = useState<string[]>([]);
    const [categoryChooserRow, setCategoryChooserRow] = useState<SextforceCaptionRow | null>(null);
    const [importCsvOpen, setImportCsvOpen] = useState(false);
    const [importCsvText, setImportCsvTextState] = useState('');
    const [importCategory, setImportCategory] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [bulkCategory, setBulkCategory] = useState('');
    const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [renameCategoryId, setRenameCategoryId] = useState('');
    const [renameCategoryName, setRenameCategoryName] = useState('');
    const [showChatGptTip, setShowChatGptTip] = useState(true);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const dismissed = window.localStorage.getItem(CAPTIONS_CHATGPT_TIP_DISMISSED_KEY) === '1';
        if (dismissed) setShowChatGptTip(false);
    }, []);

    useEffect(() => {
        if (onLoadingChange) onLoadingChange(isLoading);
    }, [isLoading, onLoadingChange]);

    const normalizedFilter = filterText.trim().toLowerCase();
    const filteredCaptions =
        normalizedFilter.length === 0
            ? captions.filter(row => categoryFilter === 'all' || (row.category || '') === categoryFilter)
            : captions.filter(
                  row =>
                      (categoryFilter === 'all' || (row.category || '') === categoryFilter) &&
                      stripHtml(row.caption).toLowerCase().includes(normalizedFilter),
              );

    const pageCount = Math.max(1, Math.ceil(filteredCaptions.length / rowsPerPage));
    const safePage = Math.min(page, pageCount - 1);
    const paginatedRows = filteredCaptions.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);
    const rangeFrom = filteredCaptions.length === 0 ? 0 : safePage * rowsPerPage + 1;
    const rangeTo = Math.min(filteredCaptions.length, (safePage + 1) * rowsPerPage);
    const allSelected = paginatedRows.length > 0 && paginatedRows.every(r => selectedIds.has(r._id));
    const someSelected = paginatedRows.some(r => selectedIds.has(r._id));

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            const next = new Set(selectedIds);
            paginatedRows.forEach(r => next.add(r._id));
            setSelectedIds(next);
        } else {
            const next = new Set(selectedIds);
            paginatedRows.forEach(r => next.delete(r._id));
            setSelectedIds(next);
        }
    };

    const handleSelectOne = (id: string) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedIds(next);
    };

    const handleAdd = () => {
        setCaptionDialogMode('add');
        setCaptionEditRow(null);
        setCaptionDialogOpen(true);
    };

    const handleCaptionDialogSubmit = async (value: string) => {
        const text = value.trim();

        if (!text) return;

        const existingCategory = captionDialogMode === 'edit' && captionEditRow ? captionEditRow.category || '' : '';

        if (captionDialogMode === 'add') {
            const created = await createCaption(text, existingCategory);

            if (created) {
                enqueueSnackbar('Caption added.', { variant: 'success' });
            }
        } else if (captionEditRow) {
            const updated = await updateCaption(captionEditRow._id, text, existingCategory);

            if (updated) {
                setCaptionEditRow(null);
                enqueueSnackbar('Caption updated.', { variant: 'success' });
            }
        }
    };

    const handleStartEdit = (row: SextforceCaptionRow) => {
        setCaptionDialogMode('edit');
        setCaptionEditRow(row);
        setCaptionDialogOpen(true);
    };

    const handleDeleteOne = (row: SextforceCaptionRow) => {
        const preview = stripHtml(row.caption);
        const snippet = preview.slice(0, 50) + (preview.length > 50 ? '…' : '');
        dialog
            .confirm({
                title: 'Delete caption',
                message: `Delete this caption? "${snippet}"`,
                ok: { text: 'Delete', color: 'error' },
                cancel: { text: 'Cancel' },
            })
            .then(() => {
                deleteCaption(row._id).then(ok => {
                    if (ok) enqueueSnackbar('Caption deleted.', { variant: 'success' });
                });
            })
            .catch(() => {});
    };

    const handleBulkDelete = () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        dialog
            .confirm({
                title: 'Bulk delete captions',
                message: `Delete ${ids.length} caption(s)?`,
                ok: { text: 'Delete', color: 'error' },
                cancel: { text: 'Cancel' },
            })
            .then(() => {
                bulkDeleteCaptions(ids).then(deleted => {
                    setSelectedIds(new Set());
                    enqueueSnackbar(`${deleted} caption(s) deleted.`, { variant: 'success' });
                });
            })
            .catch(() => {});
    };

    const handleOpenCopyToAccounts = (captionIds: string[]) => {
        if (captionIds.length === 0) return;
        // Ensure we have the latest subscriber list when opening.
        if (refetchSubscribers) refetchSubscribers();
        setCopySourceIds(captionIds);
        setCopyTargetUserId('');
        setCopyToAccountsOpen(true);
    };

    const handleCloseCopyToAccounts = () => {
        setCopyToAccountsOpen(false);
        setCopyTargetUserId('');
        setCopySourceIds([]);
    };

    const handleCopySelectedCaptions = async () => {
        const captionIds = copySourceIds;
        const targetUserId = copyTargetUserId;
        if (captionIds.length === 0 || !targetUserId) return;
        dialog
            .confirm({
                title: 'Copy captions to other account',
                message: `Copy ${captionIds.length} caption(s) to the selected account?`,
                ok: { text: 'Copy', color: 'primary' },
                cancel: { text: 'Cancel' },
            })
            .then(async () => {
                const copied = await copyCaptionsToOtherAccounts(captionIds, targetUserId);
                if (copied > 0) {
                    const targetUsername = otherAccounts.find((a: any) => a._id === targetUserId)?.username;
                    enqueueSnackbar(
                        copied === 1
                            ? `Copied 1 caption to ${targetUsername ? `@${targetUsername}` : 'target account'}.`
                            : `Copied ${copied} captions to ${targetUsername ? `@${targetUsername}` : 'target account'}.`,
                        { variant: 'success' },
                    );
                } else {
                    enqueueSnackbar('No captions were copied.', { variant: 'warning' });
                }
            })
            .catch(() => {});

        handleCloseCopyToAccounts();
    };

    const handleOpenImport = () => {
        // Default the target category to the active filter when it is a real category.
        setImportCategory(categoryFilter === 'all' ? '' : categoryFilter);
        setImportCsvOpen(true);
    };

    const handleCloseImport = () => {
        setImportCsvOpen(false);
        setImportCsvTextState('');
        setImportCategory('');
    };

    const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const text = ((reader.result as string) || '').trim();
            if (!text) {
                enqueueSnackbar('The selected file is empty.', { variant: 'warning' });
                return;
            }
            setImportCsvTextState(prev => (prev.trim() ? `${prev.trimEnd()}\n${text}` : text));
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    const handleImportPaste = async () => {
        const text = importCsvText.trim();
        if (!text) {
            enqueueSnackbar('Paste or enter one caption per line.', { variant: 'warning' });
            return;
        }
        const count = await importFromCsvText(text, importCategory);
        handleCloseImport();
        enqueueSnackbar(`${count} caption(s) imported${importCategory ? ` into "${importCategory}"` : ''}.`, { variant: 'success' });
    };

    const handleAssignSingleCategory = async (captionId: string, category: string) => {
        const updated = await assignCategory([captionId], category);
        if (updated > 0) enqueueSnackbar('Category updated.', { variant: 'success' });
    };

    const handleChooseCategoryForRow = async (category: string) => {
        if (!categoryChooserRow) return;
        const updated = await assignCategory([categoryChooserRow._id], category);
        if (updated > 0) enqueueSnackbar('Category updated.', { variant: 'success' });
        setCategoryChooserRow(null);
    };

    const handleBulkAssignCategory = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        const updated = await assignCategory(ids, bulkCategory);
        if (updated > 0) enqueueSnackbar(`Updated ${updated} caption(s).`, { variant: 'success' });
    };

    const handleCreateCategory = async () => {
        const name = newCategoryName.trim();
        if (!name) return;
        const created = await createCategory(name);
        if (created) {
            setNewCategoryName('');
            enqueueSnackbar('Category created.', { variant: 'success' });
        }
    };

    const handleRenameCategory = async () => {
        if (!renameCategoryId || !renameCategoryName.trim()) return;
        const updated = await updateCategory(renameCategoryId, renameCategoryName.trim());
        if (updated) {
            enqueueSnackbar('Category renamed.', { variant: 'success' });
            setRenameCategoryId('');
            setRenameCategoryName('');
        }
    };

    const handleDeleteCategory = (category: SextforceCaptionCategoryRow) => {
        const count = captions.filter(c => (c.category || '') === category.name).length;
        dialog
            .confirm({
                title: 'Delete category',
                message:
                    count > 0
                        ? `Delete the category "${category.name}"? ${count} caption${
                              count !== 1 ? 's' : ''
                          } will become uncategorized.`
                        : `Delete the category "${category.name}"?`,
                ok: { text: 'Delete', color: 'error' },
                cancel: { text: 'Cancel' },
            })
            .then(() => {
                deleteCategory(category._id).then(ok => {
                    if (ok) {
                        if (categoryFilter === category.name) setCategoryFilter('all');
                        if (bulkCategory === category.name) setBulkCategory('');
                        enqueueSnackbar('Category deleted.', { variant: 'success' });
                    }
                });
            })
            .catch(() => {});
    };

    return (
        <>
            <Stack spacing={1} sx={{ minHeight: 0, flex: 1, overflow: 'hidden' }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    flexWrap="wrap"
                >
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {selectedIds.size === 0 && (
                            <Grid container alignItems="center" spacing={1} wrap="nowrap">
                                <Grid item xs sm="auto">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth={isMobile}
                                        startIcon={<AddIcon />}
                                        onClick={handleAdd}
                                        sx={{
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Add Caption
                                    </Button>
                                </Grid>
                                <Grid item xs sm="auto">
                                    <Button
                                        variant="outlined"
                                        fullWidth={isMobile}
                                        startIcon={<FileUploadIcon />}
                                        onClick={handleOpenImport}
                                        disabled={importLoading}
                                        sx={{
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {isMobile ? 'Import' : 'Import Captions'}
                                    </Button>
                                </Grid>
                            </Grid>
                        )}
                        {selectedIds.size > 0 && (
                            <>
                                <Grid
                                    container
                                    alignItems="center"
                                    spacing={1}
                                    wrap="nowrap"
                                    sx={{ width: isMobile ? '100%' : 'auto' }}
                                >
                                    <Grid item xs sm="auto">
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            fullWidth={isMobile}
                                            startIcon={<ContentCopyIcon />}
                                            onClick={() => handleOpenCopyToAccounts(Array.from(selectedIds))}
                                            disabled={copyLoading || otherAccounts.length === 0}
                                            sx={{
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Copy selected ({selectedIds.size})
                                        </Button>
                                    </Grid>
                                    <Grid item xs="auto">
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            fullWidth={isMobile}
                                            color="error"
                                            startIcon={<DeleteSweepIcon />}
                                            onClick={handleBulkDelete}
                                            disabled={bulkDeleteLoading}
                                            sx={{
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Delete selected ({selectedIds.size})
                                        </Button>
                                    </Grid>
                                </Grid>
                                <Grid
                                    container
                                    alignItems="center"
                                    spacing={1}
                                    wrap="nowrap"
                                    sx={{ width: isMobile ? '100%' : 'auto' }}
                                >
                                    <Grid item xs sm="auto">
                                        <Select
                                            size="small"
                                            displayEmpty
                                            fullWidth={isMobile}
                                            value={bulkCategory}
                                            onChange={e => setBulkCategory(e.target.value)}
                                            sx={{
                                                height: 30,
                                                minWidth: 180,
                                                '& .MuiSelect-select': {
                                                    minHeight: 'auto !important',
                                                },
                                            }}
                                        >
                                            <MenuItem value="">Assign category: All</MenuItem>
                                            {categories.map(c => (
                                                <MenuItem key={c._id} value={c.name}>
                                                    {c.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>
                                    <Grid item xs="auto">
                                        <LoadingButton
                                            size="small"
                                            variant="outlined"
                                            fullWidth={isMobile}
                                            onClick={handleBulkAssignCategory}
                                            loading={categoryMutationLoading}
                                            sx={{
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Apply Category
                                        </LoadingButton>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </Stack>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    {filteredCaptions.length}
                    {filteredCaptions.length !== captions.length
                        ? ` of ${captions.length} caption${captions.length !== 1 ? 's' : ''}`
                        : ` caption${captions.length !== 1 ? 's' : ''}`}
                </Typography>
                {showChatGptTip && (
                    <Alert
                        severity="info"
                        variant="outlined"
                        sx={{ py: 0.5 }}
                        onClose={() => {
                            setShowChatGptTip(false);
                            if (typeof window !== 'undefined') {
                                window.localStorage.setItem(CAPTIONS_CHATGPT_TIP_DISMISSED_KEY, '1');
                            }
                        }}
                    >
                        <Typography variant="body2" component="span">
                            Tip: Use ChatGPT to generate 100–200 captions in your style and flavour, then use{' '}
                            <strong>Import</strong> to paste them all in at once.
                        </Typography>
                    </Alert>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search captions..."
                        value={filterText}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setFilterText(e.target.value);
                            setPage(0);
                        }}
                        sx={{ flex: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment:
                                filterText.trim().length > 0 ? (
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setFilterText('');
                                            setPage(0);
                                        }}
                                        aria-label="Clear search"
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                ) : undefined,
                        }}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === 'Escape') {
                                setFilterText('');
                                setPage(0);
                            }
                        }}
                    />
                    <Select
                        size="small"
                        value={categoryFilter}
                        onChange={e => {
                            setCategoryFilter(e.target.value);
                            setPage(0);
                        }}
                        startAdornment={
                            <InputAdornment position="start">
                                <FolderOpenIcon fontSize="small" />
                            </InputAdornment>
                        }
                        sx={{ minWidth: { sm: 220 }, height: 40 }}
                    >
                        <MenuItem value="all">All Categories</MenuItem>
                        <MenuItem value="">Uncategorized</MenuItem>
                        {categories.map(c => (
                            <MenuItem key={c._id} value={c.name}>
                                {c.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {!isMobile && (
                        <Button
                            variant="outlined"
                            startIcon={<FolderOpenIcon />}
                            onClick={() => setManageCategoriesOpen(true)}
                            sx={{ height: 40, whiteSpace: 'nowrap' }}
                        >
                            Manage Categories
                        </Button>
                    )}
                </Stack>
                <Stack direction="column" spacing={0} sx={{ minHeight: 0, flex: 1 }}>
                    {isMobile ? (
                        <>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ mb: 0.5, flexShrink: 0 }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            indeterminate={someSelected && !allSelected}
                                            checked={allSelected}
                                            onChange={handleSelectAll}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" color="text.secondary">
                                            Select all
                                        </Typography>
                                    }
                                    sx={{ mx: 0 }}
                                />
                                <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<FolderOpenIcon />}
                                    onClick={() => setManageCategoriesOpen(true)}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    Manage Categories
                                </Button>
                            </Stack>
                        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                            <Stack spacing={1}>
                                {paginatedRows.map(row => (
                                    <Paper key={row._id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <Checkbox
                                                size="small"
                                                checked={selectedIds.has(row._id)}
                                                onChange={() => handleSelectOne(row._id)}
                                                sx={{ p: 0.25, mt: 0.25 }}
                                            />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box
                                                    sx={{
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        lineHeight: 1.4,
                                                        fontSize: '0.875rem',
                                                        color: 'text.primary',
                                                    }}
                                                >
                                                    <MessageUsersFormEditiorPreview
                                                        message={row.caption}
                                                        lockMessage={false}
                                                        lockMessagePrice={0}
                                                        lockedText={false}
                                                        taggedCreators={[]}
                                                        taggedReleaseForms={[]}
                                                        fullWidth={true}
                                                        theme={theme}
                                                        showTags={false}
                                                        showTime={false}
                                                    />
                                                </Box>
                                                {(row.category || '').trim().length > 0 && (
                                                    <Chip
                                                        size="small"
                                                        label={row.category}
                                                        onClick={() => setCategoryChooserRow(row)}
                                                        sx={{ mt: 0.75 }}
                                                    />
                                                )}
                                            </Box>
                                            <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ fontVariantNumeric: 'tabular-nums', px: 0.5 }}
                                                >
                                                    {row.usedCount.toLocaleString()}
                                                </Typography>
                                                <SextforceCaptionRowLocalMenu
                                                    onEdit={() => handleStartEdit(row)}
                                                    onChangeCategory={() => setCategoryChooserRow(row)}
                                                    onCopyToAccount={() => handleOpenCopyToAccounts([row._id])}
                                                    copyDisabled={copyLoading || otherAccounts.length === 0}
                                                    onDelete={() => handleDeleteOne(row)}
                                                />
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        </Box>
                        </>
                    ) : (
                    <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            maxHeight: inDialog ? { xs: 'none', sm: 420 } : 'none',
                            overflowX: { xs: 'hidden', sm: 'auto' },
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            // checkbox type cell
                            '& .MuiTableCell-paddingCheckbox': {
                                px: 1,
                            },
                        }}
                    >
                        <Table
                            size="small"
                            stickyHeader
                            sx={{
                                minWidth: { sm: 360 },
                                tableLayout: { xs: 'fixed', sm: 'auto' },
                                '& tbody tr': { borderBottom: '1px solid' },
                                '& tbody tr:last-child': { borderBottom: 'none' },
                                '& tbody td': { borderColor: 'divider' },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        padding="none"
                                        align="center"
                                        sx={{
                                            backgroundColor: 'background.paper',
                                            zIndex: 2,
                                            borderBottom: 2,
                                            borderColor: 'divider',
                                            width: 32,
                                            py: 0.75,
                                            pl: 1,
                                            pr: 0,
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        <Checkbox
                                            indeterminate={someSelected && !allSelected}
                                            checked={allSelected}
                                            onChange={handleSelectAll}
                                            size="small"
                                            sx={{ p: 0 }}
                                        />
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            backgroundColor: 'background.paper',
                                            zIndex: 2,
                                            borderBottom: 2,
                                            borderColor: 'divider',
                                            py: 0.75,
                                            pl: 1,
                                            pr: 0,
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        Caption
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            backgroundColor: 'background.paper',
                                            zIndex: 2,
                                            borderBottom: 2,
                                            borderColor: 'divider',
                                            py: 0.75,
                                            pl: 1,
                                            pr: 0,
                                            minWidth: 50,
                                            display: { xs: 'none', sm: 'table-cell' },
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        Category
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            backgroundColor: 'background.paper',
                                            zIndex: 2,
                                            borderBottom: 2,
                                            borderColor: 'divider',
                                            py: 0.75,
                                            pl: 1,
                                            pr: 0,
                                            width: 60,
                                            display: { xs: 'none', sm: 'table-cell' },
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        Used
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            backgroundColor: 'background.paper',
                                            zIndex: 2,
                                            borderBottom: 2,
                                            borderColor: 'divider',
                                            py: 0.75,
                                            px: 1,
                                            width: 88,
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedRows.map(row => (
                                    <TableRow
                                        key={row._id}
                                        hover
                                        sx={{
                                            '& > td': { py: 0.5, verticalAlign: 'middle' },
                                            '&:hover': { backgroundColor: 'action.hover' },
                                        }}
                                    >
                                        <TableCell
                                            padding="none"
                                            align="center"
                                            sx={{
                                                py: 0.5,
                                                width: 32,
                                                pl: 1,
                                                pr: 0,
                                            }}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={selectedIds.has(row._id)}
                                                onChange={() => handleSelectOne(row._id)}
                                                sx={{ p: 0 }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 0.5, minWidth: 0, pl: 1, pr: 0 }}>
                                            <Box
                                                sx={{
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    lineHeight: 1.4,
                                                    fontSize: '0.875rem',
                                                    color: 'text.primary',
                                                }}
                                            >
                                                <MessageUsersFormEditiorPreview
                                                    message={row.caption}
                                                    lockMessage={false}
                                                    lockMessagePrice={0}
                                                    lockedText={false}
                                                    taggedCreators={[]}
                                                    taggedReleaseForms={[]}
                                                    fullWidth={true}
                                                    theme={theme}
                                                    showTags={false}
                                                    showTime={false}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                py: 0.5,
                                                pl: 1,
                                                pr: 0,
                                                minWidth: 50,
                                                display: { xs: 'none', sm: 'table-cell' },
                                            }}
                                        >
                                            <Select
                                                size="small"
                                                fullWidth
                                                displayEmpty
                                                value={row.category || ''}
                                                onChange={e => handleAssignSingleCategory(row._id, e.target.value)}
                                                renderValue={selected =>
                                                    selected && selected.toString().trim().length > 0 ? (
                                                        selected
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            All categories
                                                        </Typography>
                                                    )
                                                }
                                                sx={{
                                                    // remove minheight to make it more compact on mobile
                                                    '& .MuiSelect-select': {
                                                        minHeight: 'auto !important',
                                                        // py: 0.5,
                                                    },
                                                }}
                                            >
                                                <MenuItem value="">All</MenuItem>
                                                {categories.map(c => (
                                                    <MenuItem key={c._id} value={c.name}>
                                                        {c.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                py: 0.5,
                                                pl: 1,
                                                pr: 0,
                                                display: { xs: 'none', sm: 'table-cell' },
                                                fontVariantNumeric: 'tabular-nums',
                                            }}
                                        >
                                            {row.usedCount.toLocaleString()}
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 0.5, width: 88, px: 1 }}>
                                            <Stack direction="row" spacing={0.25} justifyContent="flex-end" flexWrap="nowrap">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleStartEdit(row)}
                                                    sx={{ minWidth: 32, minHeight: 32 }}
                                                    aria-label="Edit"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteOne(row)}
                                                    sx={{ minWidth: 32, minHeight: 32 }}
                                                    aria-label="Delete"
                                                >
                                                    <DeleteSweepIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    )}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        useFlexGap
                        spacing={1}
                        sx={{ pt: 1, pb: 0.5 }}
                    >
                        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {rangeFrom}–{rangeTo} of {filteredCaptions.length}
                        </Typography>
                        <Pagination
                            count={pageCount}
                            page={safePage + 1}
                            onChange={(_, p) => setPage(p - 1)}
                            siblingCount={isMobile ? 0 : 1}
                            boundaryCount={1}
                            size="small"
                            shape="rounded"
                        />
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                                {isMobile ? 'Rows' : 'Rows per page:'}
                            </Typography>
                            <Select
                                size="small"
                                value={rowsPerPage}
                                onChange={e => {
                                    setRowsPerPage(Number(e.target.value));
                                    setPage(0);
                                }}
                                sx={{ height: 32 }}
                            >
                                {ROWS_PER_PAGE_OPTIONS.map(o => (
                                    <MenuItem key={o} value={o}>
                                        {o}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>

            <OnlyFansMessageInputDialog
                open={captionDialogOpen}
                onClose={() => {
                    setCaptionDialogOpen(false);
                    setCaptionEditRow(null);
                }}
                onSubmit={handleCaptionDialogSubmit}
                title={captionDialogMode === 'add' ? 'Add caption' : 'Edit caption'}
                contentText={'Compose a message to use as caption for the post.'}
                initialValue={captionDialogMode === 'edit' && captionEditRow ? captionEditRow.caption : ''}
                submitButtonText={captionDialogMode === 'add' ? 'Add' : 'Save'}
                showPreview={true}
                fullScreen={isMobile}
            />

            <DialogAdaptive
                open={copyToAccountsOpen}
                onClose={handleCloseCopyToAccounts}
                maxWidth="xs"
                fullWidth
                fullScreen={isMobile}
                disableEscapeKeyDown
            >
                <StyledCardTitleBar title="Copy to other account" isLoading={isSubscribersLoading} />
                <DialogContent sx={{ px: { xs: 1.5, sm: 2 }, pt: 1, pb: 1 }}>
                    {!isSubscribersLoading && (
                        <Stack spacing={1}>
                            {otherAccounts.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No other accounts found.
                                </Typography>
                            ) : (
                                otherAccounts.map((sub: any) => {
                                    const selected = copyTargetUserId === sub._id;
                                    return (
                                        <Button
                                            key={sub._id}
                                            variant={selected ? 'contained' : 'outlined'}
                                            onClick={() => setCopyTargetUserId(sub._id)}
                                            disabled={copyLoading}
                                            sx={{ minHeight: 44, justifyContent: 'flex-start' }}
                                        >
                                            {sub.username ? `${sub.username}` : sub._id}
                                        </Button>
                                    );
                                })
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <StyledDialogActions>
                    <Button onClick={handleCloseCopyToAccounts} size="small">
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        size="small"
                        onClick={handleCopySelectedCaptions}
                        loading={copyLoading}
                        disabled={copyLoading || !copyTargetUserId || copySourceIds.length === 0}
                    >
                        Copy selected
                    </LoadingButton>
                </StyledDialogActions>
            </DialogAdaptive>

            <DialogAdaptive open={!!categoryChooserRow} onClose={() => setCategoryChooserRow(null)} maxWidth="xs" fullWidth>
                <StyledCardTitleBar title="Change category" />
                <DialogContent sx={{ px: { xs: 1.5, sm: 2 }, pt: 1, pb: 1 }}>
                    <Stack spacing={1}>
                        <Button
                            variant={!(categoryChooserRow?.category || '').trim() ? 'contained' : 'outlined'}
                            onClick={() => handleChooseCategoryForRow('')}
                            disabled={categoryMutationLoading}
                            sx={{ minHeight: 44, justifyContent: 'flex-start' }}
                        >
                            Uncategorized
                        </Button>
                        {categories.map(c => (
                            <Button
                                key={c._id}
                                variant={(categoryChooserRow?.category || '') === c.name ? 'contained' : 'outlined'}
                                onClick={() => handleChooseCategoryForRow(c.name)}
                                disabled={categoryMutationLoading}
                                sx={{ minHeight: 44, justifyContent: 'flex-start' }}
                            >
                                {c.name}
                            </Button>
                        ))}
                    </Stack>
                </DialogContent>
                <StyledDialogActions>
                    <Button onClick={() => setCategoryChooserRow(null)}>Cancel</Button>
                </StyledDialogActions>
            </DialogAdaptive>

            <Dialog open={importCsvOpen} onClose={handleCloseImport} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <StyledCardTitleBar title="Import Captions" />
                <DialogContent sx={{ px: { xs: 1.5, sm: 2 } }}>
                    <Alert severity="info" variant="outlined" sx={{ mb: 1.5, py: 0.5 }}>
                        <Typography variant="body2" component="span">
                            Generate batches of 100–200 captions in your tone with ChatGPT (or similar), then paste the list here.
                        </Typography>
                    </Alert>
                    <Stack spacing={1.5}>
                        <div>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Import into category
                            </Typography>
                            <Select
                                fullWidth
                                size="small"
                                displayEmpty
                                value={importCategory}
                                onChange={e => setImportCategory(e.target.value)}
                            >
                                <MenuItem value="">Uncategorized</MenuItem>
                                {categories.map(c => (
                                    <MenuItem key={c._id} value={c.name}>
                                        {c.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                        <Typography variant="body2" color="text.secondary">
                            Paste one caption per line, or upload a .csv/.txt file below. Empty lines are ignored.
                        </Typography>
                        <TextField
                            multiline
                            rows={isMobile ? 6 : 8}
                            fullWidth
                            placeholder="Caption 1
Caption 2
..."
                            value={importCsvText}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                setImportCsvTextState(e.target.value)
                            }
                        />
                        <div>
                            <Button
                                variant="outlined"
                                startIcon={<FileUploadIcon />}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={importLoading}
                            >
                                Upload .csv/.txt file
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.txt,text/plain"
                                style={{ display: 'none' }}
                                onChange={handleImportFile}
                            />
                        </div>
                    </Stack>
                </DialogContent>
                <StyledDialogActions>
                    <Button onClick={handleCloseImport} sx={{ minHeight: 44 }}>
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        onClick={handleImportPaste}
                        loading={importLoading}
                        disabled={!importCsvText.trim()}
                        sx={{ minHeight: 44 }}
                    >
                        Import
                    </LoadingButton>
                </StyledDialogActions>
            </Dialog>

            <Dialog open={manageCategoriesOpen} onClose={() => setManageCategoriesOpen(false)} maxWidth="sm" fullWidth>
                <StyledCardTitleBar title="Manage Caption Categories" />
                <DialogContent sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.5 }}>
                    <Stack spacing={1.5}>
                        <Typography variant="body2" color="text.secondary">
                            Categories group your captions for filtering and bulk importing. Deleting a category keeps its captions —
                            they just become uncategorized.
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="New category name"
                                value={newCategoryName}
                                onChange={e => setNewCategoryName(e.target.value)}
                                onKeyDown={(e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleCreateCategory();
                                    }
                                }}
                            />
                            <LoadingButton
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleCreateCategory}
                                loading={categoryMutationLoading}
                                disabled={!newCategoryName.trim()}
                                sx={{ whiteSpace: 'nowrap', px: 2 }}
                            >
                                Add
                            </LoadingButton>
                        </Stack>
                        {categories.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                No categories yet. Create one above to start organizing your captions.
                            </Typography>
                        ) : (
                            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                                <List dense disablePadding>
                                    {categories.map((category, index) => {
                                        const count = captions.filter(c => (c.category || '') === category.name).length;
                                        return (
                                            <ListItem
                                                key={category._id}
                                                divider={index < categories.length - 1}
                                                secondaryAction={
                                                    <Stack direction="row" spacing={0.25}>
                                                        <IconButton
                                                            size="small"
                                                            aria-label="Rename category"
                                                            onClick={() => {
                                                                setRenameCategoryId(category._id);
                                                                setRenameCategoryName(category.name);
                                                            }}
                                                            sx={{ minWidth: 32, minHeight: 32 }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            aria-label="Delete category"
                                                            onClick={() => handleDeleteCategory(category)}
                                                            disabled={categoryMutationLoading}
                                                            sx={{ minWidth: 32, minHeight: 32 }}
                                                        >
                                                            <DeleteSweepIcon fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                }
                                            >
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <FolderOpenIcon fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={category.name}
                                                    secondary={`${count} caption${count !== 1 ? 's' : ''}`}
                                                    primaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                                                />
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Paper>
                        )}
                    </Stack>
                </DialogContent>
                <StyledDialogActions>
                    <Button variant="contained" color="secondary" onClick={() => setManageCategoriesOpen(false)}>
                        Close
                    </Button>
                </StyledDialogActions>
            </Dialog>

            <Dialog open={!!renameCategoryId} onClose={() => setRenameCategoryId('')} maxWidth="xs" fullWidth>
                <StyledCardTitleBar title="Rename category" />
                <DialogContent sx={{ px: { xs: 1.5, sm: 2 }, pt: 1, pb: 1 }}>
                    <TextField
                        fullWidth
                        size="small"
                        value={renameCategoryName}
                        onChange={e => setRenameCategoryName(e.target.value)}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleRenameCategory();
                            }
                        }}
                    />
                </DialogContent>
                <StyledDialogActions>
                    <Button onClick={() => setRenameCategoryId('')}>Cancel</Button>
                    <LoadingButton onClick={handleRenameCategory} loading={categoryMutationLoading} variant="contained">
                        Save
                    </LoadingButton>
                </StyledDialogActions>
            </Dialog>
        </>
    );
}
